import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { backupsApi } from '@/lib/backups-api';
import { getErrorMessage } from '@/lib/errors';
import { flatPrimary } from '@/components/surface/tones';
import {
  endpointFor,
  providerById,
  STORAGE_PROVIDERS,
  type StorageProviderId,
} from '@/modules/backups/storage-providers';

/**
 * Adds a place for backups to go.
 *
 * ## The secret is write-only, here and everywhere after
 *
 * It is sent once and never comes back. The API returns `has_secret`, not the key — there is no
 * read path to one anywhere in the platform, so this form cannot offer to show or pre-fill it, and
 * a customer who forgets theirs replaces it rather than recovering it.
 *
 * ## Local storage is labelled for what it is
 *
 * Not "cheaper" or "simpler" — *lost with the server*. Somebody choosing it should be choosing it
 * knowingly, because the alternative is finding out during the incident it was supposed to cover.
 *
 * It stays on the list, though, and this is deliberate: it is the only way somebody with no cloud
 * storage account can take a backup at all and pull it down to their own machine. Removing it would
 * not make those customers safer, it would make them unbacked-up.
 *
 * ## Choosing a provider fills the endpoint in
 *
 * The platform has always accepted any S3-compatible endpoint, so this adds no code path and no new
 * failure mode. What it removes is the part that actually stopped people: knowing from memory that
 * Backblaze lives at `s3.eu-central-003.backblazeb2.com` and typing it into a blank box, where a
 * slip produces a failed backup with a network error instead of a hint. The endpoint stays editable
 * afterwards, because this list cannot stay right forever.
 *
 * ## Its own error, inside the dialog
 *
 * The form lives in a modal, so a refusal is shown at the top of the form rather than on the page
 * behind it, where it would be covered by the very dialog it is about.
 */
export function DestinationForm({
  onDone,
  onCancel,
}: {
  onDone: () => void;
  onCancel: () => void;
}) {
  const [providerId, setProviderId] = useState<StorageProviderId>('aws');
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [endpointTouched, setEndpointTouched] = useState(false);
  const [region, setRegion] = useState('eu-west-1');
  const [accessKeyId, setAccessKeyId] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [error, setError] = useState<string | null>(null);

  const provider = providerById(providerId);
  const kind = provider.kind;

  /**
   * The endpoint follows the provider and region until the customer edits it, and then it is
   * theirs. Overwriting something somebody typed because they went back and changed the region
   * would be the form arguing with them.
   */
  const effectiveEndpoint = endpointTouched ? endpoint : endpointFor(provider, region);

  const chooseProvider = (id: StorageProviderId) => {
    setProviderId(id);
    setEndpointTouched(false);

    const next = providerById(id);

    if (next.regionExample !== '') {
      setRegion(next.regionExample);
    }
  };

  const add = useMutation({
    mutationFn: () =>
      backupsApi.addDestination({
        name: name.trim(),
        kind,
        location: location.trim(),
        endpoint: kind === 's3' ? effectiveEndpoint.trim() : '',
        region: kind === 's3' ? region.trim() : '',
        access_key_id: kind === 's3' ? accessKeyId.trim() : '',
        secret_key: kind === 's3' ? secretKey : '',
      }),
    onSuccess: () => {
      setSecretKey('');
      onDone();
    },
    onError: (e) => setError(getErrorMessage(e)),
  });

  return (
    <div className="space-y-5">
      {error && <Alert variant="danger" className="border-0">{error}</Alert>}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="dest-provider">Where</Label>
          <Select
            id="dest-provider"
            value={providerId}
            onChange={(event) => chooseProvider(event.target.value as StorageProviderId)}
          >
            {STORAGE_PROVIDERS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="dest-name">Name</Label>
          <Input
            id="dest-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>

        <p className="-mt-2 text-xs text-muted-foreground sm:col-span-2">{provider.note}</p>
      </div>

      {kind === 'local' ? (
        <>
          <Alert variant="warning" className="flex items-start gap-2.5 border-0">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
            <span>
              The archive stays on this server, so it will not survive losing the server. Download
              each backup, or add cloud storage above.
            </span>
          </Alert>
          <div className="space-y-2">
            <Label htmlFor="dest-dir">Directory</Label>
            <Input
              id="dest-dir"
              value={location}
              onChange={(event) => setLocation(event.target.value)}
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Leave blank for /var/backups/iap. Old backups are deleted from here, so use a
              directory that holds nothing else.
            </p>
          </div>
        </>
      ) : (
        <>
          <fieldset className="space-y-4">
            <legend className="mb-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Connection
            </legend>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="dest-bucket">Bucket</Label>
                <Input
                  id="dest-bucket"
                  value={location}
                  onChange={(event) => setLocation(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dest-region">Region</Label>
                <Input
                  id="dest-region"
                  value={region}
                  onChange={(event) => setRegion(event.target.value)}
                />
                <p className="text-xs text-muted-foreground">The region code, such as {provider.regionExample}.</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dest-endpoint">Endpoint</Label>
              <Input
                id="dest-endpoint"
                value={effectiveEndpoint}
                onChange={(event) => {
                  setEndpointTouched(true);
                  setEndpoint(event.target.value);
                }}
                className="font-mono text-[13px]"
              />
              <p className="text-xs text-muted-foreground">
                {providerId === 'aws'
                  ? 'Amazon works from the region alone, so this stays empty.'
                  : 'Filled in from the provider and region. Edit it if yours differs.'}{' '}
                Must be https://. The upload carries your database.
              </p>
            </div>
          </fieldset>

          <fieldset className="space-y-4 border-t border-foreground/[0.07] pt-5">
            <legend className="sr-only">Credentials</legend>
            <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Credentials</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="dest-key">Access key ID</Label>
                <Input
                  id="dest-key"
                  value={accessKeyId}
                  onChange={(event) => setAccessKeyId(event.target.value)}
                  autoComplete="off"
                  className="font-mono text-[13px]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dest-secret">Secret access key</Label>
                <Input
                  id="dest-secret"
                  type="password"
                  value={secretKey}
                  onChange={(event) => setSecretKey(event.target.value)}
                  autoComplete="new-password"
                />
                <p className="text-xs text-muted-foreground">
                  Stored encrypted and never shown again.
                </p>
              </div>
            </div>
          </fieldset>
        </>
      )}

      <div className="flex flex-wrap justify-end gap-2 border-t border-foreground/[0.07] pt-5">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={() => add.mutate()} disabled={add.isPending || name.trim() === ''} className={flatPrimary}>
          {add.isPending && <Loader2 className="animate-spin" />}
          Add destination
        </Button>
      </div>
    </div>
  );
}
